import React, { useMemo } from 'react';
import {
  Banknote,
  TrendingUp,
  AlertTriangle,
  ArrowUpRight,
  Loader2,
  Inbox,
  ShieldCheck
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { Link } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useDashboardStats, useAuditLogs } from '@/hooks/use-api';
import { cn } from '@/lib/utils';
export function HomePage() {
  const { data: stats, isLoading: statsLoading, isFetching: statsFetching } = useDashboardStats();
  const { data: logs, isLoading: logsLoading } = useAuditLogs();
  const chartData = useMemo(() => stats?.weeklyPerformance || [], [stats?.weeklyPerformance]);
  const chartTheme = {
    grid: 'hsl(var(--border))',
    tick: 'hsl(var(--muted-foreground))',
    stroke: 'hsl(var(--primary))',
    surface: 'hsl(var(--card) / 0.96)',
    surfaceBorder: 'hsl(var(--border))',
  };
  const auditContent = useMemo(() => {
    if (logsLoading) {
      return Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="h-14 w-full bg-brand-blue-50/30 animate-pulse rounded-xl" />
      ));
    }
    const items = logs?.items || [];
    if (items.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-brand-blue-200 space-y-2">
          <Inbox className="h-8 w-8 opacity-20" />
          <p className="text-xs font-black uppercase tracking-[0.2em]">No Logged Activity</p>
        </div>
      );
    }
    return items.slice(0, 6).map((log, idx) => (
      <div key={log.id} className="flex items-start gap-4 animate-slide-up" style={{ animationDelay: `${idx * 0.05}s` }}>
        <div className={cn(
          "mt-1 h-2.5 w-2.5 rounded-full shadow-sm shrink-0",
          log.action.includes('SECURE') ? "bg-brand-blue-500 shadow-brand-blue-500/50" : "bg-brand-blue-100 shadow-brand-blue-100/30"
        )} />
        <div className="space-y-1 overflow-hidden">
          <p className="text-[10px] font-black leading-none text-brand-blue-950 uppercase tracking-tight truncate">{log.action.replace(/_/g, ' ')}</p>
          <p className="text-[10px] text-brand-blue-700 font-bold truncate tracking-tight">{log.details}</p>
          <div className="flex items-center gap-2 pt-0.5">
             <span className="text-[9px] text-brand-blue-600 font-bold">{new Date(log.timestamp).toLocaleTimeString()}</span>
             <span className="text-[8px] text-brand-blue-100">•</span>
             <span className="text-[9px] text-brand-blue-600 font-black uppercase tracking-tight">{log.actorName}</span>
          </div>
        </div>
      </div>
    ));
  }, [logs, logsLoading]);
  return (
    <AppLayout container className="bg-accent/20 scrollbar-custom">
      <div className="space-y-8 animate-fade-in">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <div className="flex items-center gap-3">
              <h1 className="text-4xl font-black tracking-tight text-foreground uppercase">
                Performance <span className="text-primary">Analytics</span>
              </h1>
              {statsFetching && (
                <div className="flex items-center gap-2 text-[10px] text-primary font-black uppercase tracking-widest bg-secondary px-3 py-1 rounded-full animate-pulse border border-border">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Live Syncing
                </div>
              )}
            </div>
            <p className="text-muted-foreground mt-2 font-bold uppercase tracking-widest text-[10px]">Enterprise oversight for institutional portfolios.</p>
          </div>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <Card className="border-none shadow-soft group rounded-2xl transition-all hover:shadow-glow-lg">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em]">Liquid Deposits</CardTitle>
              <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center border border-border">
                <Banknote className="h-4 w-4 text-primary" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-black text-foreground">
                {statsLoading ? <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /> : `GHS ${stats?.totalCollections.toLocaleString()}`}
              </div>
              <p className="text-[10px] text-muted-foreground font-black flex items-center mt-3 uppercase tracking-tight">
                <ArrowUpRight className="h-3 w-3 mr-1" />
                Verified Inflow
              </p>
            </CardContent>
          </Card>
          <Card className="border-none shadow-soft group rounded-2xl transition-all hover:shadow-glow-lg">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em]">Active Exposure</CardTitle>
              <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center border border-border">
                <TrendingUp className="h-4 w-4 text-primary" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-black text-foreground">{statsLoading ? <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /> : stats?.activeLoansCount}</div>
              <p className="text-[10px] text-muted-foreground font-bold mt-3 uppercase tracking-tight">
                Yield standing: <span className="text-primary font-black">STABLE</span>
              </p>
            </CardContent>
          </Card>
          <Card className="border-none shadow-soft group rounded-2xl transition-all hover:shadow-glow-lg">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em]">Portfolio Risk</CardTitle>
              <div className="h-8 w-8 rounded-lg bg-destructive/10 flex items-center justify-center border border-destructive/20">
                <AlertTriangle className="h-4 w-4 text-destructive" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-black text-foreground">{statsLoading ? <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /> : `${stats?.defaultRiskRate}%`}</div>
              <p className="text-[10px] text-destructive font-black mt-3 uppercase tracking-tight">
                Risk Index active
              </p>
            </CardContent>
          </Card>
          <Card className="border-none shadow-soft bg-primary text-primary-foreground group hover:shadow-glow transition-all rounded-2xl">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-[10px] font-black text-primary-foreground uppercase tracking-[0.2em]">Total Liquidity</CardTitle>
              <ShieldCheck className="h-4 w-4 text-primary-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-black text-primary-foreground">
                {statsLoading ? <Loader2 className="h-6 w-6 animate-spin text-primary-foreground/50" /> : `GHS ${stats?.cashOnHand.toLocaleString()}`}
              </div>
               <p className="text-[10px] text-primary-foreground font-black mt-3 uppercase tracking-tight">
                 Audited & Secured
               </p>
            </CardContent>
          </Card>
        </div>
        <div className="grid gap-6 lg:grid-cols-7">
          <Card className="lg:col-span-4 overflow-hidden rounded-[2rem] border border-border shadow-soft">
            <CardHeader className="pb-0 p-8">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-xl font-black text-foreground uppercase tracking-tight">Inflow Statistics</CardTitle>
                  <CardDescription className="font-bold text-[10px] uppercase tracking-widest text-muted-foreground">Daily transaction density distribution.</CardDescription>
                </div>
                <div className="h-12 w-12 rounded-2xl bg-muted flex items-center justify-center border border-border">
                  <TrendingUp className="h-6 w-6 text-primary" />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="h-[350px] min-h-[350px] w-full px-8 pb-8 pt-4" style={{ minHeight: '350px', height: '350px' }}>
                {statsLoading ? (
                  <div className="flex h-full w-full items-center justify-center rounded-3xl bg-muted/50">
                    <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
                  </div>
                ) : chartData.length === 0 ? (
                  <div className="flex h-full w-full items-center justify-center rounded-3xl bg-muted/50">
                    <p className="text-muted-foreground text-sm font-medium">No data available</p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={350} minHeight={350}>
                    <AreaChart data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorPerf" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={chartTheme.stroke} stopOpacity={0.22}/>
                          <stop offset="95%" stopColor={chartTheme.stroke} stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartTheme.grid} />
                      <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{fill: chartTheme.tick, fontSize: 10, fontWeight: 900}} dy={15} />
                      <YAxis hide />
                      <Tooltip
                        contentStyle={{
                          borderRadius: '16px',
                          border: `1px solid ${chartTheme.surfaceBorder}`,
                          boxShadow: '0 10px 25px -5px rgba(12,63,110,0.18)',
                          background: chartTheme.surface,
                          backdropFilter: 'blur(8px)'
                        }}
                      />
                      <Area type="monotone" dataKey="amount" stroke={chartTheme.stroke} strokeWidth={4} fillOpacity={1} fill="url(#colorPerf)" />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>
          <Card className="lg:col-span-3 overflow-hidden rounded-[2rem] border border-border shadow-glass">
            <CardHeader className="flex flex-row items-center justify-between bg-muted/30 border-b border-border p-8">
              <div>
                <CardTitle className="text-xl font-black text-foreground uppercase tracking-tight">Security Ledger</CardTitle>
                <CardDescription className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">Audit History</CardDescription>
              </div>
              <Button variant="ghost" size="sm" className="rounded-xl text-[10px] font-black text-primary hover:bg-muted/50 uppercase tracking-widest px-4 border border-border" asChild>
                <Link to="/audit-logs">View Logs</Link>
              </Button>
            </CardHeader>
            <CardContent className="p-8 scrollbar-custom overflow-y-auto max-h-[350px]">
              <div className="space-y-8">
                {auditContent}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
